import { ChangeDetectionStrategy, Component, output, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-aula5-section',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      <!-- Breadcrumb Navigation & Top Action Bar -->
      <div class="flex flex-wrap items-center justify-between gap-4">
        <button 
          type="button" 
          (click)="navigateBack.emit()" 
          class="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold text-[#F27D26] border border-white/10 transition-all cursor-pointer">
          <span class="material-symbols-outlined text-base">arrow_back</span>
          <span>Voltar ao Menu de Aulas</span>
        </button>

        <div class="flex flex-wrap items-center gap-3">
          <!-- View Switcher Tabs -->
          <div class="flex items-center space-x-1 bg-[#121214] p-1.5 rounded-xl border border-white/10">
            <button 
              type="button"
              (click)="onSelectView('slides')"
              [class]="activeView() === 'slides' 
                ? 'bg-[#F27D26] text-black font-bold shadow-md shadow-[#F27D26]/20' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'"
              class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">slideshow</span>
              <span>Slides (10 Slides)</span>
            </button>

            <button 
              type="button"
              (click)="onSelectView('activity')"
              [class]="activeView() === 'activity' 
                ? 'bg-[#F27D26] text-black font-bold shadow-md shadow-[#F27D26]/20' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'"
              class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">assignment</span>
              <span>Folha de Atividades Completa</span>
            </button>
          </div>
        </div>
      </div>

      <!-- VIEW 1: INLINE SLIDES PRESENTATION CARD -->
      @if (activeView() === 'slides') {
        <div class="bg-[#121214] rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-10 min-h-[580px] flex flex-col justify-between relative overflow-hidden">
          <!-- Slide Glow Accent -->
          <div class="absolute -top-24 -right-24 w-80 h-80 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

          <!-- Top Banner Bar -->
          <div class="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-white/10">
            <div class="flex items-center space-x-2">
              <span class="material-symbols-outlined text-[#F27D26] text-xl">slideshow</span>
              <span class="text-xs font-mono font-semibold text-zinc-300">Modo de Leitura Integrado</span>
            </div>
            
            <button 
              type="button" 
              (click)="openPresentationModal()" 
              class="px-3 py-1.5 rounded-lg bg-[#F27D26]/15 hover:bg-[#F27D26]/25 text-[#F27D26] border border-[#F27D26]/30 text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-sm">present_to_all</span>
              <span>Apresentação Imersiva</span>
            </button>
          </div>

          <!-- SLIDES CONTENT -->
          <div class="my-6">
            <!-- SLIDE 1 -->
            @if (currentSlide() === 0) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">movie</span>
                  <span>ESTUDO DE CASO • FOME DE PODER (THE FOUNDER)</span>
                </span>

                <div>
                  <h1 class="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                    O Modelo McDonald's Aplicado à Infraestrutura e Redes
                  </h1>
                  <p class="text-lg text-zinc-400 font-medium mt-1">
                    Como arquitetura física, eliminação de gargalos e visão de escala moldam gigantes de tecnologia.
                  </p>
                </div>

                <div class="bg-[#0A0A0B] rounded-xl p-6 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3">
                  <h3 class="text-lg font-bold text-white flex items-center space-x-2">
                    <span>🎬</span>
                    <span>Sinopse e Contextualização:</span>
                  </h3>
                  <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                    O filme narra a trajetória de <strong>Ray Kroc</strong> e dos irmãos <strong>Richard e Maurice McDonald</strong>. Os irmãos criaram o revolucionário <em>Speedee Service System</em> — uma inovação radical de processos, arquitetura física e eliminação de gargalos. No entanto, foi a visão de escala, persistência, busca de oportunidades e modelo de negócios de Ray Kroc que transformou uma lanchonete local em um império global.
                  </p>
                </div>
              </div>
            }

            <!-- SLIDE 2 -->
            @if (currentSlide() === 1) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">draw</span>
                  <span>BLOCO 1 • CAMADA FÍSICA & PROCESSOS</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  A Quadra de Tênis: Engenharia de Layout e Baixa Latência
                </h2>
                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  Os irmãos desenharam a cozinha a giz no chão para cronometrar cada movimento dos chapeiros e montadores.
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-sky-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-sky-400 flex items-center space-x-2">
                      <span>🍔</span>
                      <span>Na Cozinha do McDonald's</span>
                    </h3>
                    <p class="text-zinc-300 text-xs leading-relaxed text-justify">
                      Distâncias milimétricas entre chapa, molho, pão e embalagem. Zero colisões de operadores, reduzindo o tempo de entrega de 30 minutos para 30 segundos.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-emerald-400 flex items-center space-x-2">
                      <span>🖥️</span>
                      <span>No Rack de Cabeamento Estruturado</span>
                    </h3>
                    <p class="text-zinc-300 text-xs leading-relaxed text-justify">
                      Patch panels identificados, rotas de cabos organizadas sem cruzamentos desnecessários e patch cords certificados para manutenção rápida (baixo MTTR) e zero atenuação.
                    </p>
                  </div>
                </div>

                <div class="bg-[#F27D26]/10 border border-[#F27D26]/30 rounded-xl p-4 text-center text-xs font-mono font-bold text-[#F27D26]">
                  PRINCÍPIO TÉCNICO: Layout físico eficiente reduz latência humana e de pacotes, elimina gargalos e garante escalabilidade!
                </div>
              </div>
            }

            <!-- SLIDE 3 -->
            @if (currentSlide() === 2) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">search_check</span>
                  <span>BLOCO 1 • DORES DO CLIENTE</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Diagnóstico de Dores: Drive-in Tradicional vs. Speedee System
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-rose-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-rose-400">❌ As Dores do Drive-in Antigo</h3>
                    <ul class="text-zinc-300 text-xs space-y-1.5 list-disc pl-4 text-justify">
                      <li>Espera longa de 30 minutos no carro.</li>
                      <li>Pedidos vinham trocados ou frios constantemente.</li>
                      <li>Pratos, copos de vidro e talheres eram furtados ou quebrados (alto prejuízo).</li>
                      <li>Público desordeiro afastando famílias.</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-emerald-400">✅ A Solução de Processo dos Irmãos</h3>
                    <ul class="text-zinc-300 text-xs space-y-1.5 list-disc pl-4 text-justify">
                      <li>Cliente desce do carro e pede direto no balcão.</li>
                      <li>Embalagens de papel 100% descartáveis (zero louça e zero roubo).</li>
                      <li>Hambúrguer montado e embalado em 30 segundos com padrão industrial.</li>
                      <li>Ambiente familiar focado em agilidade e higiene.</li>
                    </ul>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 4 -->
            @if (currentSlide() === 3) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">psychology</span>
                  <span>BLOCO 2 • AS 10 CCES EM AÇÃO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  O Duelo Comportamental: Irmãos McDonald vs. Ray Kroc
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-sky-500/40 space-y-3">
                    <div class="flex items-center space-x-2">
                      <span class="text-2xl">👨‍🍳👨‍🍳</span>
                      <h3 class="text-base font-bold text-sky-400">Irmãos Dick & Mac McDonald</h3>
                    </div>
                    <div class="text-xs text-zinc-300 space-y-2 text-justify">
                      <p><strong class="text-white">Ponto Forte:</strong> <em>Exigência de Qualidade e Eficiência (CCE 3)</em> levada ao extremo do perfeccionismo.</p>
                      <p><strong class="text-rose-400">Ponto Fraco:</strong> Pavor de <em>Correr Riscos Calculados (CCE 2)</em> e falta de <em>Estabelecimento de Metas de Escala (CCE 7)</em> por medo de perder o controle direto.</p>
                    </div>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-[#F27D26]/50 space-y-3">
                    <div class="flex items-center space-x-2">
                      <span class="text-2xl">💼</span>
                      <h3 class="text-base font-bold text-[#F27D26]">Ray Kroc</h3>
                    </div>
                    <div class="text-xs text-zinc-300 space-y-2 text-justify">
                      <p><strong class="text-white">Ponto Forte:</strong> <em>Busca de Oportunidades (CCE 1)</em>, <em>Persistência Implacável (CCE 4)</em> e <em>Persuasão (CCE 9)</em> para expandir franquias.</p>
                      <p><strong class="text-amber-400">Destaque:</strong> Capacidade de transformar uma operação local num modelo de escala mundial.</p>
                    </div>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 5 -->
            @if (currentSlide() === 4) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">trending_up</span>
                  <span>BLOCO 2 • AS CCES DE RAY KROC</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Ray Kroc: A Prática das CCEs de Realização e Poder
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-white/10 space-y-2">
                    <span class="px-2 py-0.5 rounded text-[10px] bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold uppercase">CCE 1</span>
                    <h3 class="text-sm font-bold text-white">Iniciativa & Oportunidade</h3>
                    <p class="text-zinc-400 text-xs text-justify">
                      Ao receber o pedido de 8 máquinas de milk-shake, não apenas despachou os produtos: viajou centenas de quilômetros para ver pessoalmente por que aquela lanchonete vendia tanto.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-white/10 space-y-2">
                    <span class="px-2 py-0.5 rounded text-[10px] bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold uppercase">CCE 4</span>
                    <h3 class="text-sm font-bold text-white">Persistência Rigorosa</h3>
                    <p class="text-zinc-400 text-xs text-justify">
                      Quando franqueados ricos começaram a vender frango frito e desrespeitar a padronização, Ray Kroc rompeu contratos e selecionou casais de classe média com perfil trabalhador.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-4 rounded-xl border border-white/10 space-y-2">
                    <span class="px-2 py-0.5 rounded text-[10px] bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold uppercase">CCE 2 & 7</span>
                    <h3 class="text-sm font-bold text-white">Riscos Calculados & Metas</h3>
                    <p class="text-zinc-400 text-xs text-justify">
                      Mudou o modelo de negócios: o McDonald's passou a comprar os terrenos das lojas e alugá-los aos franqueados, garantindo receita imobiliária previsível.
                    </p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 6 -->
            @if (currentSlide() === 5) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">content_cut</span>
                  <span>BLOCO 3 • POSICIONAMENTO ESTRATÉGICO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  O Corte Radical do Cardápio: De 27 para 3 Itens
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-rose-500/40 space-y-2">
                    <h3 class="text-sm font-bold text-rose-400">A Armadilha do Cardápio com 27 Itens</h3>
                    <p class="text-zinc-300 text-xs leading-relaxed text-justify">
                      Churrasco, pratos diversos, sobremesas variadas. Cozinha caótica, estoque caro e desperdício de insumos. A tentativa de agradar todo mundo gerava lentidão e margens apertadas.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-emerald-500/40 space-y-2">
                    <h3 class="text-sm font-bold text-emerald-400">O Foco em 87% do Faturamento</h3>
                    <p class="text-zinc-300 text-xs leading-relaxed text-justify">
                      Descobriram que 87% das vendas vinham de: <strong>Hambúrguer, Batata Frita e Refrigerante</strong>. Cortaram todo o resto e atingiram hiper-eficiência operacional.
                    </p>
                  </div>
                </div>

                <div class="bg-[#0A0A0B] p-4 rounded-xl border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-1">
                  <h4 class="text-xs font-bold text-[#F27D26] uppercase">Lição para Consultoria de Redes:</h4>
                  <p class="text-xs text-zinc-300 text-justify">
                    Tentar ser o "técnico faz-tudo" (arrumar celular, trocar tela, recarregar cartucho) dilui sua autoridade e mantém você na guerra de preços. Foque nos serviços de maior valor agregado (redundância MikroTik, firewall e contratos mensais de suporte).
                  </p>
                </div>
              </div>
            }

            <!-- SLIDE 7 -->
            @if (currentSlide() === 6) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">badge</span>
                  <span>BLOCO 3 • INTRAEMPREENDEDORISMO REAL</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Intraempreendedorismo: Os Casos Fred Turner & Harry Sonneborn
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-white/10 space-y-3">
                    <div class="flex items-center space-x-2">
                      <span class="text-2xl">👨‍🍳</span>
                      <h3 class="text-sm font-bold text-[#F27D26]">Fred Turner (De Chapeiro a Presidente Global)</h3>
                    </div>
                    <p class="text-zinc-300 text-xs leading-relaxed text-justify">
                      Começou na chapa limpando grelhas. Por sua postura proativa de propor manuais de padronização rigorosa e treinamento de franqueados, tornou-se o braço direito de Ray Kroc e posteriormente CEO global do McDonald's.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] p-5 rounded-xl border border-white/10 space-y-3">
                    <div class="flex items-center space-x-2">
                      <span class="text-2xl">📊</span>
                      <h3 class="text-sm font-bold text-sky-400">Harry Sonneborn (O Estrategista Imobiliário)</h3>
                    </div>
                    <p class="text-zinc-300 text-xs leading-relaxed text-justify">
                      Como consultor financeiro contratado, identificou que o negócio não era apenas vender hambúrguer, mas controlar os pontos comerciais (terrenos). Essa inovação interna salvou a empresa da falência e criou o império imobiliário da marca.
                    </p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 8 -->
            @if (currentSlide() === 7) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">gavel</span>
                  <span>BLOCO 4 • DILEMA ÉTICO & CONTRATOS</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  O "Acordo de Cavalheiros" e os Riscos da Informalidade
                </h2>

                <div class="bg-[#0A0A0B] p-6 rounded-xl border-l-4 border-rose-500 border-y border-r border-white/10 space-y-3">
                  <h3 class="text-sm font-bold text-rose-400 flex items-center space-x-2">
                    <span>🤝❌</span>
                    <span>O Erro dos Irmãos McDonald:</span>
                  </h3>
                  <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                    Na venda final da marca, os irmãos aceitaram um acordo verbal de 1% dos royalties perpétuos da corporação apenas no "aperto de mãos" (sem inclusão no contrato oficial por exigência dos investidores). <strong>Resultado: Nunca receberam um centavo desses royalties, perdendo centenas de milhões de dólares.</strong>
                  </p>
                </div>

                <div class="bg-indigo-950/40 border border-indigo-500/30 p-4 rounded-xl text-xs text-zinc-300 leading-relaxed text-justify">
                  ⚖️ <strong>Regra de Ouro em TIC:</strong> No mercado de redes e suporte gerenciado (MSP), nunca confie em combinações verbais com clientes. Sempre formalize escopo de trabalho, limites de atendimento, SLAs e valores em contratos jurídicos assinados.
                </div>
              </div>
            }

            <!-- SLIDE 9 -->
            @if (currentSlide() === 8) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">hub</span>
                  <span>SÍNTESE • DA COZINHA AO DATA CENTER</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Quadro Comparativo: Speedee System vs. Infraestrutura de Redes
                </h2>

                <div class="overflow-x-auto">
                  <table class="w-full text-xs text-left border-collapse border border-white/10 rounded-xl overflow-hidden">
                    <thead>
                      <tr class="bg-[#0A0A0B] text-[#F27D26] font-mono border-b border-white/10">
                        <th class="p-3 border-r border-white/10">Conceito do Filme (The Founder)</th>
                        <th class="p-3">Equivalente em Redes de Computadores</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-white/10 text-zinc-300">
                      <tr class="bg-[#121214]">
                        <td class="p-3 border-r border-white/10 font-medium text-white">Layout da cozinha na quadra de tênis</td>
                        <td class="p-3 text-justify">Topologia de rede planejada e Rack de cabeamento estruturado organizado.</td>
                      </tr>
                      <tr class="bg-[#0A0A0B]">
                        <td class="p-3 border-r border-white/10 font-medium text-white">Eliminação de pratos e talheres de vidro</td>
                        <td class="p-3 text-justify">Eliminação de pontos únicos de falha (SPOF) e substituição de hardware obsoleto.</td>
                      </tr>
                      <tr class="bg-[#121214]">
                        <td class="p-3 border-r border-white/10 font-medium text-white">Corte de 27 para 3 itens no cardápio</td>
                        <td class="p-3 text-justify">Especialização em nichos de alto valor (Link Duplo, Cibersegurança, MSP).</td>
                      </tr>
                      <tr class="bg-[#0A0A0B]">
                        <td class="p-3 border-r border-white/10 font-medium text-white">Manual rigoroso de padronização da loja</td>
                        <td class="p-3 text-justify">Documentação de rede, políticas de segurança e Acordos de Nível de Serviço (SLA).</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            }

            <!-- SLIDE 10 -->
            @if (currentSlide() === 9) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">verified</span>
                  <span>SÍNTESE DA AULA</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  A Grande Lição do Dia
                </h2>

                <div class="bg-gradient-to-r from-[#F27D26]/20 via-[#F27D26]/10 to-transparent rounded-2xl p-6 sm:p-8 border border-[#F27D26]/40 text-center">
                  <h3 class="text-lg sm:text-xl font-extrabold text-[#F27D26] leading-relaxed">
                    "Criar uma solução técnica brilhante é apenas a primeira metade do caminho.<br><br>
                    É a visão de processos, padronização, modelo de negócio e persistência estratégica que constrói um legado de sucesso."
                  </h3>
                </div>

                <div class="bg-[#0A0A0B] rounded-xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-3">
                  <h3 class="text-base font-bold text-white flex items-center space-x-2">
                    <span>🚀</span>
                    <span>Próximo Passo:</span>
                  </h3>
                  <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                    Abra a <strong>Folha de Atividades</strong> e responda aos 4 Blocos de análise conectando o filme "Fome de Poder" aos desafios de infraestrutura, comportamento empreendedor e posicionamento em Redes!
                  </p>
                  <div class="pt-2">
                    <button 
                      type="button" 
                      (click)="onSelectView('activity')" 
                      class="px-4 py-2 rounded-lg bg-[#F27D26] text-black font-bold text-xs hover:bg-[#e06c17] transition-all flex items-center space-x-1.5 cursor-pointer">
                      <span class="material-symbols-outlined text-base">assignment</span>
                      <span>Ir para Folha de Atividades Completa</span>
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- Slide Controls Footer -->
          <div class="pt-4 border-t border-white/10 flex items-center justify-between">
            <button 
              type="button" 
              (click)="prevSlide()" 
              [disabled]="currentSlide() === 0"
              class="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 text-xs font-semibold text-white border border-white/10 transition-all flex items-center space-x-1 cursor-pointer">
              <span class="material-symbols-outlined text-base">chevron_left</span>
              <span>Anterior</span>
            </button>

            <div class="flex items-center space-x-3">
              <span class="text-xs text-zinc-400 font-mono font-medium">
                Slide {{ currentSlide() + 1 }} de {{ totalSlides }}
              </span>
              <div class="w-24 sm:w-36 h-2 bg-white/10 rounded-full overflow-hidden hidden sm:block">
                <div 
                  class="h-full bg-[#F27D26] transition-all duration-300"
                  [style.width.%]="((currentSlide() + 1) / totalSlides) * 100">
                </div>
              </div>
            </div>

            <button 
              type="button" 
              (click)="nextSlide()" 
              [disabled]="currentSlide() === totalSlides - 1"
              class="px-4 py-2 rounded-xl bg-[#F27D26] hover:bg-[#e06c17] disabled:opacity-30 disabled:hover:bg-[#F27D26] text-xs font-bold text-black transition-all flex items-center space-x-1 cursor-pointer">
              <span>Próximo</span>
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      }

      <!-- VIEW 2: FULL INTERACTIVE ACTIVITY SHEET -->
      @if (activeView() === 'activity') {
        <div class="bg-[#121214] rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-10 space-y-8">
          <!-- Header Bar with Print Button -->
          <div class="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-white/10">
            <div>
              <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider mb-2">
                <span class="material-symbols-outlined text-sm">movie</span>
                <span>ESTUDO DE CASO CINEMATOGRÁFICO</span>
              </span>
              <h1 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                🎬 Atividade de Análise: "Fome de Poder" (The Founder)
              </h1>
              <p class="text-xs sm:text-sm text-zinc-400 mt-1">
                O Modelo McDonald's Aplicado à Infraestrutura, Processos e Redes de Computadores
              </p>
            </div>

            <button 
              type="button" 
              (click)="printActivitySheet()" 
              class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-all flex items-center space-x-2 cursor-pointer shadow-lg shadow-emerald-600/20">
              <span class="material-symbols-outlined text-base">print</span>
              <span>Imprimir / Salvar PDF</span>
            </button>
          </div>

          <!-- Context Card -->
          <div class="bg-[#0A0A0B] p-5 rounded-xl border border-white/10 space-y-2">
            <h3 class="text-sm font-bold text-[#F27D26] flex items-center space-x-2">
              <span class="material-symbols-outlined text-base">auto_stories</span>
              <span>📖 Sinopse e Contextualização:</span>
            </h3>
            <p class="text-xs text-zinc-300 leading-relaxed text-justify">
              O filme narra a trajetória de <strong>Ray Kroc</strong> e dos irmãos <strong>Richard e Maurice McDonald</strong>. Os irmãos criaram o revolucionário Sistema de Serviço Rápido (<em>Speedee Service System</em>) — uma inovação radical de processos, arquitetura física e eliminação de gargalos. No entanto, foi a visão de escala, persistência, busca de oportunidades e modelo de negócios de Ray Kroc que transformou uma lanchonete local em um império global.
            </p>
          </div>

          <!-- BLOCO 1 -->
          <div class="space-y-4 pt-2">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 BLOCO 1: Engenharia de Processos & Infraestrutura Física (Aulas 1 e 2)</span>
            </h2>
            <p class="text-xs text-zinc-400 italic text-justify">
              (Paralelo com Cabeamento Estruturado, Topologia de Rede e Eliminação de Gargalos) • <em>Cena de Referência:</em> Os irmãos McDonald desenham a planta da cozinha com giz em uma quadra de tênis, testando e cronometrando cada passo dos funcionários até chegarem ao layout perfeito (hambúrguer entregue em 30 segundos em vez de 30 minutos).
            </p>

            <div class="space-y-3 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                1. Otimização de Camada Física e Redução de Latência:
              </label>
              <p class="text-xs text-zinc-300 text-justify">
                Na quadra de tênis, os irmãos McDonald reorganizaram os equipamentos físicos para eliminar passos desnecessários e colisões entre os operadores.
              </p>

              <div class="space-y-2 pl-2">
                <p class="text-xs text-zinc-300 text-justify">
                  <strong>a)</strong> Faça um paralelo técnico: Como essa organização milimétrica da cozinha se compara à montagem de um Rack de Cabeamento Estruturado (Patch Panel, organizadores, mini-racks e cabos certificados) em uma empresa?
                </p>
                <textarea 
                  rows="3" 
                  [(ngModel)]="answers.q1_a" 
                  placeholder="Digite sua resposta aqui..." 
                  class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>

                <p class="text-xs text-zinc-300 text-justify pt-2">
                  <strong>b)</strong> Qual é o prejuízo para um negócio quando a infraestrutura física (seja uma cozinha ou uma sala de servidores) é tratada com desorganização (o "ninho de ratos" visto na Aula 2)?
                </p>
                <textarea 
                  rows="3" 
                  [(ngModel)]="answers.q1_b" 
                  placeholder="Digite sua resposta aqui..." 
                  class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
              </div>
            </div>

            <div class="space-y-2 pt-3">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                2. Diagnóstico de Dores de Mercado (Dor Real vs. Solução):
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                Antes do McDonald's, os Drive-ins tradicionais nos EUA tinham três grandes dores: pedidos demoravam 30 minutos, vinham errados e os pratos/talheres de vidro eram roubados ou quebrados.
              </p>
              <p class="text-xs text-zinc-300 text-justify">
                Identifique a <strong>Dor Real de Negócio</strong> dos clientes e a <strong>Solução de Infraestrutura/Processo</strong> criada pelos irmãos McDonald para zerar esse problema:
              </p>
              <textarea 
                rows="4" 
                [(ngModel)]="answers.q2" 
                placeholder="Identifique as dores reais e as soluções criadas..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>
          </div>

          <!-- BLOCO 2 -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 BLOCO 2: Diagnóstico das 10 CCEs – O Duelo de Perfis (Aula 3)</span>
            </h2>
            <p class="text-xs text-zinc-400 italic text-justify">
              (Características do Comportamento Empreendedor: Irmãos McDonald vs. Ray Kroc)
            </p>

            <div class="space-y-3 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                3. Mapeamento Comportamental dos Irmãos McDonald:
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                Os irmãos Dick e Mac criaram o sistema mais eficiente do mundo, mas tinham pavor de abrir franquias com medo de perder o controle de qualidade.
              </p>

              <div class="space-y-2 pl-2">
                <p class="text-xs text-zinc-300 text-justify">
                  <strong>a)</strong> Qual CCE do Conjunto de Realização os irmãos dominavam com maestria?
                </p>
                <input 
                  type="text" 
                  [(ngModel)]="answers.q3_a" 
                  placeholder="Ex: CCE 3 - Exigência de Qualidade e Eficiência..." 
                  class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none">

                <p class="text-xs text-zinc-300 text-justify pt-2">
                  <strong>b)</strong> Qual CCE do Conjunto de Planejamento/Realização faltou aos irmãos para que eles mesmos expandissem o negócio em escala nacional?
                </p>
                <input 
                  type="text" 
                  [(ngModel)]="answers.q3_b" 
                  placeholder="Ex: CCE 2 - Correr Riscos Calculados / CCE 7 - Estabelecimento de Metas..." 
                  class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none">
              </div>
            </div>

            <div class="space-y-3 pt-3">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                4. Mapeamento Comportamental de Ray Kroc:
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                Ray Kroc começou vendendo máquinas de milk-shake aos 52 anos, enfrentou dezenas de recusas de bancos e hipotecou a própria casa para bancar a expansão.
              </p>
              <p class="text-xs text-zinc-300 text-justify">
                Identifique cenas do filme que exemplifiquem as seguintes CCEs demonstradas por Ray Kroc:
              </p>

              <div class="space-y-3 pl-2">
                <div class="space-y-1">
                  <label class="block text-xs font-bold text-[#F27D26]">• Busca de Oportunidades e Iniciativa:</label>
                  <p class="text-xs text-zinc-400">O que ele fez ao receber o pedido de 8 máquinas de milk-shake de uma única lanchonete no interior da Califórnia?</p>
                  <textarea 
                    rows="2" 
                    [(ngModel)]="answers.q4_oportunidade" 
                    placeholder="Sua resposta..." 
                    class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none"></textarea>
                </div>

                <div class="space-y-1">
                  <label class="block text-xs font-bold text-[#F27D26]">• Persistência:</label>
                  <p class="text-xs text-zinc-400">Como ele reagiu quando os primeiros franqueados ricos começaram a desrespeitar o padrão e vender frango frito?</p>
                  <textarea 
                    rows="2" 
                    [(ngModel)]="answers.q4_persistencia" 
                    placeholder="Sua resposta..." 
                    class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none"></textarea>
                </div>

                <div class="space-y-1">
                  <label class="block text-xs font-bold text-[#F27D26]">• Correr Riscos Calculados & Estabelecimento de Metas:</label>
                  <p class="text-xs text-zinc-400">Como a mudança do modelo de negócio — de lanchonete para empresa imobiliária de terrenos — garantiu o fluxo de caixa da empresa?</p>
                  <textarea 
                    rows="2" 
                    [(ngModel)]="answers.q4_riscos" 
                    placeholder="Sua resposta..." 
                    class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none"></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- BLOCO 3 -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 BLOCO 3: Tipos de Empreendedorismo e Posicionamento de Mercado (Aula 4)</span>
            </h2>
            <p class="text-xs text-zinc-400 italic text-justify">
              (O "Faz-Tudo Genérico" vs. O Especialista Focado)
            </p>

            <div class="space-y-3 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                5. Posicionamento Estratégico (O Corte Radical do Cardápio):
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                No início, os irmãos McDonald tinham um cardápio com 27 itens (incluindo churrasco e pratos diversos), mas perceberam que 87% do faturamento vinha de apenas 3 itens: hambúrguer, batata frita e refrigerante. Eles fecharam o restaurante por meses e cortaram todo o resto do cardápio.
              </p>

              <div class="space-y-2 pl-2">
                <p class="text-xs text-zinc-300 text-justify">
                  <strong>a)</strong> Relacione essa decisão ao conceito de Posicionamento de Mercado visto na Aula 4 (A armadilha do técnico "faz-tudo" vs. O especialista em nichos lucrativos):
                </p>
                <textarea 
                  rows="3" 
                  [(ngModel)]="answers.q5_a" 
                  placeholder="Digite sua resposta..." 
                  class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>

                <p class="text-xs text-zinc-300 text-justify pt-2">
                  <strong>b)</strong> Por que tentar atender a "todo mundo com qualquer serviço" enfraquece a lucratividade de uma consultoria de redes?
                </p>
                <textarea 
                  rows="3" 
                  [(ngModel)]="answers.q5_b" 
                  placeholder="Digite sua resposta..." 
                  class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
              </div>
            </div>

            <div class="space-y-2 pt-3">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                6. Modelos de Atuação e Intraempreendedorismo:
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                Durante a expansão das franquias, Ray Kroc conheceu Fred Turner (que começou como chapeiro limpando grelhas e, por sua postura proativa de propor melhorias, tornou-se presidente global da corporação) e Harry Sonneborn (o consultor financeiro que criou a estratégia de compra de terrenos).
              </p>
              <p class="text-xs text-zinc-300 text-justify">
                Explique como a atuação de Fred Turner e Harry Sonneborn exemplifica o conceito de <strong>Intraempreendedorismo</strong> (gerar inovações e soluções de alto impacto dentro de uma organização):
              </p>
              <textarea 
                rows="4" 
                [(ngModel)]="answers.q6" 
                placeholder="Sua explicação sobre o intraempreendedorismo de Fred Turner e Harry Sonneborn..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>
          </div>

          <!-- BLOCO 4 -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 BLOCO 4: Dilema Ético e Negociação Comercial (Síntese)</span>
            </h2>

            <div class="space-y-3 pt-2">
              <label class="block text-xs sm:text-sm font-bold text-white text-justify">
                7. O Contrato de Cavalheiros e a Importância da Formalização:
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                No final do filme, os irmãos McDonald aceitam um acordo verbal de 1% dos royalties perpétuos da empresa com um "aperto de mãos" (Handshake Deal), sem colocar essa cláusula no contrato formal por questões fiscais e jurídicas. O resultado foi que eles nunca receberam esses royalties.
              </p>
              <p class="text-xs text-zinc-300 text-justify">
                Com base no que discutimos nas Aulas 2, 3 e 4 sobre Contratos de Suporte Gerenciado (MSP), Acordos de Nível de Serviço (SLA) e Formalização (MEI/PJ):<br>
                <strong>Por que o profissional técnico de TI nunca deve confiar em acordos informais de boca com clientes, e sim documentar escopo, valores e penalidades em contrato formal assinado?</strong>
              </p>
              <textarea 
                rows="4" 
                [(ngModel)]="answers.q7" 
                placeholder="Escreva sua análise técnica e ética sobre formalização de contratos em TI..." 
                class="w-full bg-[#0A0A0B] border border-white/10 rounded-xl p-3 text-xs text-zinc-200 focus:border-[#F27D26] focus:outline-none transition-all"></textarea>
            </div>
          </div>
        </div>
      }

      <!-- FULLSCREEN IMMERSIVE PRESENTATION OVERLAY MODAL -->
      @if (isPresentationModalOpen()) {
        <div class="fixed inset-0 z-[100] bg-[#0A0A0B] backdrop-blur-2xl p-4 sm:p-8 flex flex-col justify-between select-none overflow-hidden h-screen w-screen max-h-screen max-w-screen animate-in fade-in zoom-in-95 duration-200">
          
          <!-- Top Bar inside Overlay -->
          <div class="max-w-6xl w-full mx-auto flex items-center justify-between pb-4 border-b border-white/10 text-white">
            <div class="flex items-center space-x-3">
              <span class="w-9 h-9 rounded-xl bg-[#F27D26]/20 border border-[#F27D26]/40 text-[#F27D26] font-bold flex items-center justify-center">
                <span class="material-symbols-outlined text-xl">slideshow</span>
              </span>
              <div>
                <h3 class="text-sm font-bold text-white tracking-wide">Fome de Poder (The Founder): Processos, Infraestrutura e Redes</h3>
              </div>
            </div>
          </div>

          <!-- Centered 16:9 Presentation Canvas Container -->
          <div class="max-w-6xl w-full mx-auto my-auto bg-[#121214] border border-white/15 rounded-3xl p-6 sm:p-12 shadow-2xl shadow-black/90 min-h-[460px] sm:min-h-[540px] flex flex-col justify-center relative overflow-hidden">
            <div class="absolute top-0 right-0 w-96 h-96 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

            <!-- SLIDES CONTENT IN OVERLAY -->
            <div class="my-auto">
              <!-- SLIDE 1 -->
              @if (currentSlide() === 0) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">movie</span>
                    <span>ESTUDO DE CASO • FOME DE PODER (THE FOUNDER)</span>
                  </span>

                  <div>
                    <h1 class="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                      O Modelo McDonald's Aplicado à Infraestrutura e Redes
                    </h1>
                    <p class="text-lg sm:text-xl text-zinc-400 font-medium mt-2">
                      Como arquitetura física, eliminação de gargalos e visão de escala moldam gigantes de tecnologia.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 sm:p-8 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                    <h3 class="text-xl font-bold text-white flex items-center space-x-2">
                      <span>🎬</span>
                      <span>Sinopse e Contextualização:</span>
                    </h3>
                    <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                      O filme narra a trajetória de <strong>Ray Kroc</strong> e dos irmãos <strong>Richard e Maurice McDonald</strong>. Os irmãos criaram o revolucionário <em>Speedee Service System</em> — uma inovação radical de processos, arquitetura física e eliminação de gargalos. No entanto, foi a visão de escala, persistência, busca de oportunidades e modelo de negócios de Ray Kroc que transformou uma lanchonete local em um império global.
                    </p>
                  </div>
                </div>
              }

              <!-- SLIDE 2 -->
              @if (currentSlide() === 1) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">draw</span>
                    <span>BLOCO 1 • CAMADA FÍSICA & PROCESSOS</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    A Quadra de Tênis: Engenharia de Layout e Baixa Latência
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-sky-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-sky-400 flex items-center space-x-2">
                        <span>🍔</span>
                        <span>Na Cozinha do McDonald's</span>
                      </h3>
                      <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                        Distâncias milimétricas entre chapa, molho, pão e embalagem. Zero colisões de operadores, reduzindo o tempo de entrega de 30 minutos para 30 segundos.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400 flex items-center space-x-2">
                        <span>🖥️</span>
                        <span>No Rack de Cabeamento Estruturado</span>
                      </h3>
                      <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                        Patch panels identificados, rotas de cabos organizadas sem cruzamentos desnecessários e patch cords certificados para manutenção rápida (baixo MTTR) e zero atenuação.
                      </p>
                    </div>
                  </div>

                  <div class="bg-[#F27D26]/10 rounded-2xl p-4 border border-[#F27D26]/40 text-center font-mono text-xs sm:text-sm font-bold text-[#F27D26] leading-relaxed shadow-lg">
                    PRINCÍPIO TÉCNICO: Layout físico eficiente reduz latência humana e de pacotes, elimina gargalos e garante escalabilidade!
                  </div>
                </div>
              }

              <!-- SLIDE 3 -->
              @if (currentSlide() === 2) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">search_check</span>
                    <span>BLOCO 1 • DORES DO CLIENTE</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Diagnóstico de Dores: Drive-in Tradicional vs. Speedee System
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-rose-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-rose-400">❌ As Dores do Drive-in Antigo</h3>
                      <ul class="text-zinc-300 text-xs sm:text-sm space-y-1.5 list-disc pl-4 text-justify">
                        <li>Espera longa de 30 minutos no carro.</li>
                        <li>Pedidos vinham trocados ou frios constantemente.</li>
                        <li>Pratos, copos de vidro e talheres eram furtados ou quebrados (alto prejuízo).</li>
                        <li>Público desordeiro afastando famílias.</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400">✅ A Solução de Processo dos Irmãos</h3>
                      <ul class="text-zinc-300 text-xs sm:text-sm space-y-1.5 list-disc pl-4 text-justify">
                        <li>Cliente desce do carro e pede direto no balcão.</li>
                        <li>Embalagens de papel 100% descartáveis (zero louça e zero roubo).</li>
                        <li>Hambúrguer montado e embalado em 30 segundos com padrão industrial.</li>
                        <li>Ambiente familiar focado em agilidade e higiene.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 4 -->
              @if (currentSlide() === 3) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">psychology</span>
                    <span>BLOCO 2 • AS 10 CCES EM AÇÃO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    O Duelo Comportamental: Irmãos McDonald vs. Ray Kroc
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-sky-500/40 space-y-3 shadow-xl">
                      <div class="flex items-center space-x-2">
                        <span class="text-2xl">👨‍🍳👨‍🍳</span>
                        <h3 class="text-base sm:text-lg font-bold text-sky-400">Irmãos Dick & Mac McDonald</h3>
                      </div>
                      <div class="text-xs sm:text-sm text-zinc-300 space-y-2 text-justify">
                        <p><strong class="text-white">Ponto Forte:</strong> <em>Exigência de Qualidade e Eficiência (CCE 3)</em> levada ao extremo do perfeccionismo operacional.</p>
                        <p><strong class="text-rose-400">Ponto Fraco:</strong> Pavor de <em>Correr Riscos Calculados (CCE 2)</em> e falta de <em>Estabelecimento de Metas de Escala (CCE 7)</em> por medo de perder o controle direto.</p>
                      </div>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-[#F27D26]/60 space-y-3 shadow-xl">
                      <div class="flex items-center space-x-2">
                        <span class="text-2xl">💼</span>
                        <h3 class="text-base sm:text-lg font-bold text-[#F27D26]">Ray Kroc</h3>
                      </div>
                      <div class="text-xs sm:text-sm text-zinc-300 space-y-2 text-justify">
                        <p><strong class="text-white">Ponto Forte:</strong> <em>Busca de Oportunidades (CCE 1)</em>, <em>Persistência Implacável (CCE 4)</em> e <em>Persuasão (CCE 9)</em> para expandir franquias.</p>
                        <p><strong class="text-amber-400">Destaque:</strong> Capacidade de transformar uma operação local num modelo de escala mundial.</p>
                      </div>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 5 -->
              @if (currentSlide() === 4) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">trending_up</span>
                    <span>BLOCO 2 • AS CCES DE RAY KROC</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Ray Kroc: A Prática das CCEs de Realização e Poder
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2 shadow-xl">
                      <span class="px-2.5 py-1 rounded text-xs bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold uppercase">CCE 1</span>
                      <h3 class="text-sm font-bold text-white">Iniciativa & Oportunidade</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Ao receber o pedido de 8 máquinas de milk-shake, não apenas despachou os produtos: viajou centenas de quilômetros para ver pessoalmente por que aquela lanchonete vendia tanto.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2 shadow-xl">
                      <span class="px-2.5 py-1 rounded text-xs bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold uppercase">CCE 4</span>
                      <h3 class="text-sm font-bold text-white">Persistência Rigorosa</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Quando franqueados ricos começaram a vender frango frito e desrespeitar a padronização, Ray Kroc rompeu contratos e selecionou casais de classe média com perfil trabalhador.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2 shadow-xl">
                      <span class="px-2.5 py-1 rounded text-xs bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold uppercase">CCE 2 & 7</span>
                      <h3 class="text-sm font-bold text-white">Riscos Calculados & Metas</h3>
                      <p class="text-zinc-400 text-xs leading-relaxed text-justify">
                        Mudou o modelo de negócios: o McDonald's passou a comprar os terrenos das lojas e alugá-los aos franqueados, garantindo receita imobiliária previsível.
                      </p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 6 -->
              @if (currentSlide() === 5) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">content_cut</span>
                    <span>BLOCO 3 • POSICIONAMENTO ESTRATÉGICO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    O Corte Radical do Cardápio: De 27 para 3 Itens
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-rose-500/40 space-y-2 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-rose-400">A Armadilha do Cardápio com 27 Itens</h3>
                      <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                        Churrasco, pratos diversos, sobremesas variadas. Cozinha caótica, estoque caro e desperdício de insumos. A tentativa de agradar todo mundo gerava lentidão e margens apertadas.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-emerald-500/40 space-y-2 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400">O Foco em 87% do Faturamento</h3>
                      <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                        Descobriram que 87% das vendas vinham de: <strong>Hambúrguer, Batata Frita e Refrigerante</strong>. Cortaram todo o resto e atingiram hiper-eficiência operacional.
                      </p>
                    </div>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-4 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-1 shadow-lg">
                    <h4 class="text-xs sm:text-sm font-bold text-[#F27D26] uppercase">Lição para Consultoria de Redes:</h4>
                    <p class="text-xs sm:text-sm text-zinc-300 text-justify">
                      Tentar ser o "técnico faz-tudo" (arrumar celular, trocar tela, recarregar cartucho) dilui sua autoridade e mantém você na guerra de preços. Foque nos serviços de maior valor agregado (redundância MikroTik, firewall e contratos mensais de suporte).
                    </p>
                  </div>
                </div>
              }

              <!-- SLIDE 7 -->
              @if (currentSlide() === 6) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">badge</span>
                    <span>BLOCO 3 • INTRAEMPREENDEDORISMO REAL</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Intraempreendedorismo: Os Casos Fred Turner & Harry Sonneborn
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-3 shadow-xl">
                      <div class="flex items-center space-x-2">
                        <span class="text-2xl">👨‍🍳</span>
                        <h3 class="text-base sm:text-lg font-bold text-[#F27D26]">Fred Turner (De Chapeiro a Presidente Global)</h3>
                      </div>
                      <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                        Começou na chapa limpando grelhas. Por sua postura proativa de propor manuais de padronização rigorosa e treinamento de franqueados, tornou-se o braço direito de Ray Kroc e posteriormente CEO global do McDonald's.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-3 shadow-xl">
                      <div class="flex items-center space-x-2">
                        <span class="text-2xl">📊</span>
                        <h3 class="text-base sm:text-lg font-bold text-sky-400">Harry Sonneborn (O Estrategista Imobiliário)</h3>
                      </div>
                      <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                        Como consultor financeiro contratado, identificou que o negócio não era apenas vender hambúrguer, mas controlar os pontos comerciais (terrenos). Essa inovação interna salvou a empresa da falência e criou o império imobiliário da marca.
                      </p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 8 -->
              @if (currentSlide() === 7) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">gavel</span>
                    <span>BLOCO 4 • DILEMA ÉTICO & CONTRATOS</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    O "Acordo de Cavalheiros" e os Riscos da Informalidade
                  </h2>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-rose-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                    <h3 class="text-base sm:text-lg font-bold text-rose-400 flex items-center space-x-2">
                      <span>🤝❌</span>
                      <span>O Erro dos Irmãos McDonald:</span>
                    </h3>
                    <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                      Na venda final da marca, os irmãos aceitaram um acordo verbal de 1% dos royalties perpétuos da corporação apenas no "aperto de mãos" (sem inclusão no contrato oficial por exigência dos investidores). <strong>Resultado: Nunca receberam um centavo desses royalties, perdendo centenas de milhões de dólares.</strong>
                    </p>
                  </div>

                  <div class="bg-indigo-950/40 border border-indigo-500/30 p-5 rounded-2xl text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
                    ⚖️ <strong>Regra de Ouro em TIC:</strong> No mercado de redes e suporte gerenciado (MSP), nunca confie em combinações verbais com clientes. Sempre formalize escopo de trabalho, limites de atendimento, SLAs e valores em contratos jurídicos assinados.
                  </div>
                </div>
              }

              <!-- SLIDE 9 -->
              @if (currentSlide() === 8) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">hub</span>
                    <span>SÍNTESE • DA COZINHA AO DATA CENTER</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Quadro Comparativo: Speedee System vs. Infraestrutura de Redes
                  </h2>

                  <div class="overflow-x-auto">
                    <table class="w-full text-xs text-left border-collapse border border-white/10 rounded-2xl overflow-hidden shadow-xl">
                      <thead>
                        <tr class="bg-[#0A0A0B] text-[#F27D26] font-mono border-b border-white/10">
                          <th class="p-3.5 border-r border-white/10">Conceito do Filme (The Founder)</th>
                          <th class="p-3.5">Equivalente em Redes de Computadores</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-white/10 text-zinc-300">
                        <tr class="bg-[#121214]">
                          <td class="p-3.5 border-r border-white/10 font-medium text-white">Layout da cozinha na quadra de tênis</td>
                          <td class="p-3.5 text-justify">Topologia de rede planejada e Rack de cabeamento estruturado organizado.</td>
                        </tr>
                        <tr class="bg-[#0A0A0B]">
                          <td class="p-3.5 border-r border-white/10 font-medium text-white">Eliminação de pratos e talheres de vidro</td>
                          <td class="p-3.5 text-justify">Eliminação de pontos únicos de falha (SPOF) e substituição de hardware obsoleto.</td>
                        </tr>
                        <tr class="bg-[#121214]">
                          <td class="p-3.5 border-r border-white/10 font-medium text-white">Corte de 27 para 3 itens no cardápio</td>
                          <td class="p-3.5 text-justify">Especialização em nichos de alto valor (Link Duplo, Cibersegurança, MSP).</td>
                        </tr>
                        <tr class="bg-[#0A0A0B]">
                          <td class="p-3.5 border-r border-white/10 font-medium text-white">Manual rigoroso de padronização da loja</td>
                          <td class="p-3.5 text-justify">Documentação de rede, políticas de segurança e Acordos de Nível de Serviço (SLA).</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              }

              <!-- SLIDE 10 -->
              @if (currentSlide() === 9) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">verified</span>
                    <span>SÍNTESE DA AULA</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    A Grande Lição do Dia
                  </h2>

                  <div class="bg-gradient-to-r from-[#F27D26]/20 via-[#F27D26]/10 to-transparent rounded-2xl p-6 sm:p-10 border border-[#F27D26]/50 text-center shadow-2xl">
                    <h3 class="text-xl sm:text-2xl lg:text-3xl font-extrabold text-[#F27D26] leading-relaxed">
                      "Criar uma solução técnica brilhante é apenas a primeira metade do caminho.<br><br>
                      É a visão de processos, padronização, modelo de negócio e persistência estratégica que constrói um legado de sucesso."
                    </h3>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-4 shadow-xl">
                    <h3 class="text-lg font-bold text-white flex items-center space-x-2">
                      <span>🚀</span>
                      <span>Próximo Passo:</span>
                    </h3>
                    <p class="text-zinc-300 text-xs sm:text-base leading-relaxed text-justify">
                      Abra a <strong>Folha de Atividades</strong> e responda aos 4 Blocos de análise conectando o filme "Fome de Poder" aos desafios de infraestrutura, comportamento empreendedor e posicionamento em Redes!
                    </p>
                    <div>
                      <button 
                        type="button" 
                        (click)="closePresentationModal(); onSelectView('activity')" 
                        class="px-5 py-2.5 rounded-xl bg-[#F27D26] text-black font-bold text-xs sm:text-sm hover:bg-[#e06c17] transition-all flex items-center space-x-2 cursor-pointer shadow-lg shadow-[#F27D26]/20">
                        <span class="material-symbols-outlined text-base">assignment</span>
                        <span>Ir para Folha de Atividades Completa</span>
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Bottom Floating Control Dock (Gamma Style) Identical to Aula 3 -->
          <div class="max-w-3xl w-full mx-auto bg-[#121214]/95 backdrop-blur border border-white/15 rounded-2xl p-2.5 shadow-2xl flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:space-x-3">
            <button 
              type="button" 
              (click)="prevSlide()" 
              [disabled]="currentSlide() === 0"
              class="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 text-xs font-semibold text-white border border-white/10 transition-all flex items-center space-x-1 cursor-pointer shrink-0">
              <span class="material-symbols-outlined text-base">chevron_left</span>
              <span class="hidden sm:inline">Anterior</span>
            </button>

            <!-- Interactive Slide Jump Buttons (Pílulas Numeradas 1 a 10) -->
            <div class="flex items-center justify-center gap-1 flex-wrap">
              @for (slideNum of slideNumbers; track slideNum) {
                <button 
                  type="button" 
                  (click)="goToSlide(slideNum)"
                  [class]="currentSlide() === slideNum 
                    ? 'bg-[#F27D26] text-black font-bold scale-110 shadow-md shadow-[#F27D26]/30' 
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'"
                  class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg text-[11px] font-mono transition-all flex items-center justify-center cursor-pointer">
                  {{ slideNum + 1 }}
                </button>
              }
            </div>

            <button 
              type="button" 
              (click)="nextSlide()" 
              [disabled]="currentSlide() === totalSlides - 1"
              class="px-3.5 py-2 rounded-xl bg-[#F27D26] hover:bg-[#e06c17] disabled:opacity-30 disabled:hover:bg-[#F27D26] text-xs font-bold text-black transition-all flex items-center space-x-1 cursor-pointer shrink-0">
              <span class="hidden sm:inline">Próximo</span>
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      }
    </section>
  `
})
export class Aula5SectionComponent {
  navigateBack = output<void>();

  activeView = signal<'slides' | 'activity'>('slides');
  currentSlide = signal<number>(0);
  totalSlides = 10;
  slideNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

  isPresentationModalOpen = signal<boolean>(false);

  answers = {
    q1_a: '',
    q1_b: '',
    q2: '',
    q3_a: '',
    q3_b: '',
    q4_oportunidade: '',
    q4_persistencia: '',
    q4_riscos: '',
    q5_a: '',
    q5_b: '',
    q6: '',
    q7: ''
  };

  onSelectView(view: 'slides' | 'activity') {
    this.activeView.set(view);
    if (view === 'slides') {
      this.openPresentationModal();
    }
  }

  nextSlide() {
    if (this.currentSlide() < this.totalSlides - 1) {
      this.currentSlide.update(c => c + 1);
    }
  }

  prevSlide() {
    if (this.currentSlide() > 0) {
      this.currentSlide.update(c => c - 1);
    }
  }

  goToSlide(slideIndex: number) {
    if (slideIndex >= 0 && slideIndex < this.totalSlides) {
      this.currentSlide.set(slideIndex);
    }
  }

  openPresentationModal() {
    this.isPresentationModalOpen.set(true);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    }
  }

  closePresentationModal() {
    this.isPresentationModalOpen.set(false);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    }
  }

  printActivitySheet() {
    if (typeof window !== 'undefined') {
      window.print();
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (this.isPresentationModalOpen() || this.activeView() === 'slides') {
      if (event.key === 'ArrowRight' || event.key === ' ') {
        this.nextSlide();
      } else if (event.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (event.key === 'Escape') {
        this.closePresentationModal();
      }
    }
  }
}
